import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionForm } from './ActionForm';
import { getJiraComponents, getOrgAliases, showForm, addItem, hasChanged, getContracts, searchPackages, getGrants } from '../../store/formSlice';
import { noneToNull } from '../../utils';
import { clone, filter, find, findIndex, flatten } from 'lodash';
import { useLocation } from 'react-router';
import { ECO_RATING, GRANTS, KITSTYLE, MINOS, SWTYPES, SYSARCH } from '../../settings';
import { registerPackage } from '../../store/uploadSlice';
import { pingServer } from '../../store/userSlice';


const ActionFormContainer = () => {
    const dispatch = useDispatch();
    const entity = useSelector(state => state.form.visible);
    const { values, aliases, loading, components, hasChanges, contracts, packages, grants } = useSelector(state => state.form);
    const { details } = useSelector(state => state.data);

    let alias, email;
    const parts = useLocation().pathname.split("/"),
    aliasIdx = parts.indexOf("organisations") + 1,
    emailIdx = parts.indexOf("users") + 1;
    if (aliasIdx > 0) alias = parts[aliasIdx];
    if (emailIdx > 0) email = parts[emailIdx];
    const editMode = Object.keys(values).length > 0;

    const initialState = {
        values: {},
        errors: {}
    };

    const [ state, setState ] = useState(initialState);

    useEffect(() => {
        if (entity && Object.keys(formFields).indexOf(entity) > -1) {
            dispatch(pingServer());
            setState({ values: editMode ? values : formFields[entity].initialValues ? formFields[entity].initialValues : {}, errors: {} });
            const interval = setInterval(() => {
                dispatch(pingServer())
            }, 900000)

            return () => {
                setState(initialState);
                clearInterval(interval)
            };
        } else {
            setState(initialState);
        };
    }, [ entity ]);

    useEffect(() => {
        if ((entity === "users" || entity === "grants" || entity === "contracts") && !aliases.length) {
            dispatch(getOrgAliases());
        }
        if ((entity === "users" || entity === "grants") && !grants.length) {
            dispatch(getGrants());
        }
        else if (entity === "issues" && !components.length) {
            dispatch(getJiraComponents());
        }
        else if (entity === "prereq") {
            dispatch(searchPackages(values.sysarch));
        };
        if ((entity === "contracts" || entity === "restrictions" || entity === "packages") && !contracts.length) {
            dispatch(getContracts());
        };
    }, [ entity, dispatch, aliases.length ]);

    const formFields = {
        organisations: {
            title: editMode ? "Edit Organisation Info" : "Add Organisation",
            fields: [
                {
                    key: "name",
                    type: "input",
                    label: "Organisation Name",
                    rules: [
                        { required: true }
                    ]
                },
                {
                    key: "alias",
                    type: "input",
                    label: "Organisation alias",
                    max: 3,
                    disabled: editMode,
                    rules: [
                        { required: true },
                        { pattern: /^[a-z]{3}$/, message: "Alias must contain only 3 lowercase letters." }
                    ]
                },
                {
                    key: "addr",
                    type: "input",
                    label: "Address"
                },
                [
                    {
                        key: "phone",
                        type: "input",
                        label: "Phone",
                        rules: [
                            { pattern: /^[0-9+() -]*$/, message: "Invalid phone number." }
                        ]
                    },
                    {
                        key: "email",
                        type: "input",
                        label: "Contact E-Mail",
                        rules: [
                            { pattern: /^[a-zA-Z0-9._%+-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,125}[a-zA-Z]{2,63}$/, message: "Invalid e-mail." }
                        ]
                    }
                ],
                {
                    key: "notes",
                    type: "text",
                    label: "notes",
                }
            ]
        },
        users: {
            title: editMode ? "Edit User Info" : "Add User",
            fields: [
                {
                    key: "email",
                    type: "input",
                    label: "E-Mail",
                    max: 60,
                    rules: [
                        { required: true },
                        { pattern: /^[a-zA-Z0-9._%+-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,125}[a-zA-Z]{2,63}$/, message: "Invalid e-mail." }
                    ],
                    disabled: editMode
                },
                {
                    key: "name",
                    type: "input",
                    label: "Full User Name",
                    max: 60,
                    rules: [
                        { required: true },
                        { pattern: /^[a-z ,.'-]+$/i, message: "User name can contain only Latin letters, spaces, commas, dots, dashes, and apostrophes." }
                    ]
                },
                {
                    key: "orgAlias",
                    type: "select",
                    label: "Default Organisation",
                    options: editMode && Array.isArray(details.grants) ? details.grants.map(grant => ({ key: grant.orgAlias, title: `[${grant.orgAlias}] ${grant.orgName}`})) : aliases,
                    search: true,
                    disabled: alias && !editMode,
                    loading: loading,
                    rules: [
                        { required: true }
                    ]
                },
                {
                    key: "grant",
                    type: "select",
                    label: "Grant Name",
                    options: grants,
                    rules: [
                        { required: true }
                    ],
                    disabled: editMode
                },
            ],
            initialValues: {
                email: null,
                name: null,
                orgAlias: alias,
                grant: "partner",
                expiry: undefined
            }
        },
        issues: {
            title: "Create Issue",
            fields: [
                {
                    key: "summary",
                    type: "input",
                    label: "Summary",
                    max: 255,
                    rules: [
                        { required: true },
                        { pattern: /^(?!\s*$).+/, message: "The summary can contain at least 1 character and a maximum length of 255 characters." }
                    ]
                },
                {
                    key: "description",
                    type: "text",
                    label: "Description",
                    rules: [
                        { required: true },
                        { pattern: /^(?!\s*$).+/, message: "The description can contain at least 1 character." }
                    ]
                },
                [
                    {
                        key: "product",
                        type: "select",
                        options: components,
                        label: "Product",
                        loading: loading,
                        rules: [
                            { required: true }
                        ]
                    },
                    {
                        key: "version",
                        type: "input",
                        label: "Product version"
                    }
                ],
                [
                    {
                        key: "osversion",
                        type: "select",
                        options: MINOS,
                        label: "OS Version",
                        rules: [
                            { required: true }
                        ]
                    },
                    {
                        key: "sysarch",
                        type: "select",
                        options: SYSARCH,
                        label: "System architecture",
                        rules: [
                            { required: true }
                        ]
                    }
                ],
                {
                    key: "log",
                    type:"file",
                    label: "VSI$SUPPORT.COM Script Output",
                    extraLabel: <a href="https://vmssoftware.com/products/vsisupport" target="_blank">More info...</a>
                },
                {
                    key: "file",
                    type: "file",
                    label: "Attachments"
                }
            ]
        },
        grants: {
            width: 320,
            title: editMode ? "Edit Grant" : "Issue a grant",
            fields: [
                {
                    key: "email",
                    type: "input",
                    label: "E-Mail",
                    disabled: true
                },
                {
                    key: "orgAlias",
                    type: "select",
                    label: "Organisation",
                    options: aliases,
                    search: true,
                    disabled: editMode,
                    loading: loading,
                    rules: [
                        { required: true }
                    ]
                },
                {
                    key: "grantName",
                    type: "select",
                    label: "Grant Name",
                    options: details.orgAlias === "vsi" ? grants : filter(grants, { visible: true }),
                    rules: [
                        { required: true }
                    ]
                },
                {
                    key: "setExpiration",
                    type: "checkbox",
                    label: "Set Expiration date"
                },
                state.values.setExpiration
                ? {
                    key: "expiryDate",
                    type: "date",
                    label: "Expiration date",
                    rules: [
                        { required: state.values.setExpiration }
                    ]
                }
                : {}
            ],
            initialValues: {
                email: email,
                orgAlias: null,
                grantName: "partner",
                setExpiration: false
                // expiryDate: null
            }
        },
        contracts: {
            width: 320,
            title: editMode ? "Edit Contract" : "Apply Contract",
            fields: [
                {
                    key: "alias",
                    label: "Organisation alias",
                    type: "select",
                    options: aliases,
                    search: true,
                    disabled: !aliases.length || editMode || alias,
                    loading: loading,
                    rules: [
                        { required: true }
                    ]
                },
                {
                    key: "name",
                    type: "select",
                    label: "Contract name",
                    options: filter(contracts, c => c.active),
                    loading: loading,
                    disabled: !contracts.length || editMode,
                    rules: [
                        { required: true }
                    ]
                },
                {
                    key: "endDate",
                    type: "date",
                    label: "Expiration date",
                    rules: [
                        { required: true }
                    ]
                }
            ],
            initialValues: {
                alias: alias
            }
        },
        packages: {
            width: 520,
            title: editMode ? "Update Package Info" : "Register Package",
            fields: [
                {
                    key: "name",
                    type: "input",
                    label: "Package Name",
                    max: 39,
                    uppercase: "true",
                    rules: [
                        {
                            pattern: /^[A-Za-z0-9_\-.]*$/,
                            message: "The package name can contain only uppercase letters, numbers, and underscores and has a maximum length of 39 characters."
                        },
                        {
                            required: true
                        }
                    ]
                },
                [
                    {
                        key: "version",
                        type: "input",
                        label: "Package Version",
                        max: 16,
                        uppercase: "true",
                        rules: [
                            {
                                pattern: /^([A-Z]\d+)\.(\d+)\-([A-Z0-9])+(\-[A-Z0-9]+)*$/,
                                message: 'Specify the version number with up to 15 characters as Vx.x-e-e, where V is an uppercase Latin letter, x is a combination of numbers, and e is a combination of uppercase alphanumeric characters (last e is optional). Ex: V1.0-1A.'
                            },
                            {
                                required: true
                            }
                        ]
                    },
                    {
                        key: "sysarch",
                        type:"select",
                        label: "System Architecture",
                        options: SYSARCH,
                        rules: [
                            {
                                required: true
                            }
                        ]
                    },
                    {
                        key: "minos",
                        type:"select",
                        label: "Minimum OS",
                        options: MINOS,
                        rules: [
                            {
                                required: true
                            }
                        ]
                    },
                ],
                [
                    {
                        key: "style",
                        type:"select",
                        label: "Kit Style",
                        options: KITSTYLE,
                        rules: [
                            {
                                required: true
                            }
                        ]
                    },
                    {
                        key: "swtype",
                        type:"select",
                        label: "Kit Type",
                        options: SWTYPES,
                        rules: [
                            {
                                required: true
                            }
                        ]
                    },
                    {
                        key: "ecoRating",
                        type: "select",
                        label: "ECO Kit Rating",
                        options: ECO_RATING,
                        disabled: state.values.swtype !== "e",
                        rules: [
                            {
                                required: state.values.swtype === "e"
                            }
                        ]
                    }
                ],
                editMode
                ? {}
                : {
                    key: "contract",
                    type:"select",
                    label: "Package Restrictions",
                    options: contracts
                },
                {
                    key: "files",
                    type:"file",
                    label: "File",
                    disabled: editMode,
                    rules: [
                        {
                            key: "name",
                            pattern: /^[A-Za-z0-9_\-.$]*$/,
                            message: "The file name can contain only Latin letters, numbers, dots, $, hyphens and underscores."
                        },
                        {
                            required: true
                        }
                    ],
                    multiple: true
                },
                {
                    key: "description",
                    type:"text",
                    label: "Description",
                    rules: [
                        {
                            required: true
                        }
                    ]
                },
                {
                    key: "restart",
                    type: "checkbox",
                    label: "Restart required"
                }
            ],
            initialValues: {
                restart: false
            }
        },
        restrictions: {
            width: 360,
            title: "Package restrictions",
            okButtonText: "Save",
            fields: [
                {
                    key: "contracts",
                    multiple: true,
                    type: "select",
                    label: "Contract",
                    options: filter(contracts, contract => findIndex(state.values.contracts, { name: contract.title }) < 0),
                    loading: loading,
                    disabled: !contracts.length,
                }
            ]
        },
        prereq: {
            width: 360,
            okButtonText: "Save",
            title: "Package Prerequisites",
            fields: [
                {
                    key: "prereq",
                    multiple: true,
                    label: "Prerequisite",
                    type: "select",
                    options: filter(packages, p => p.verified == 1 && p.key != state.values.key && findIndex(state.values.prereq, pr => pr && pr.packageNumber == p.key) < 0 ).map(d => ({ key: d.key, title: d.name })),
                    loading: loading,
                    disabled: !packages.length
                }
            ]
        },
        contract: {
            width: 320,
            title: "Create Contract",
            fields: [
                {
                    key: "name",
                    type: "input",
                    label: "Contract name",
                    rules: [
                        { required: true },
                        { pattern: /^[a-zA-Z0-9-]*$/, message: "Invalid Contract name." }
                    ]
                },
                {
                    key: "description",
                    type: "text",
                    label: "description",
                }
            ]
        }
    };

    const onValueChange = (key, value, idx) => {
        const { fields } = formFields[entity];
        const { values } = state;
        const item = find(flatten(fields), { key: key });
        let newval;

        if (item) {
            if (item.multiple) {
                newval = Array.isArray(values[key]) ? values[key].slice() : [];
                if (value === null) {
                    if (idx > -1) newval.splice(idx, 1);
                } else {
                    if (key === "prereq") {
                        let pkg = find(packages, { key: value });
                        newval.push({
                            installationOrder: values[key].length + 1,
                            packageNumber: Number(value),
                            name: pkg ? pkg.name : null,
                            version: pkg ? pkg.version : null
                        })
                    } else {
                        if (key === "files") {
                            newval.push(value);
                        } else {
                            newval.push({ name: value });
                        }
                    }
                }
            } else {
                newval = value === "" ? null : value;
            };

            setState(state => ({
                ...state,
                values: {
                ...state.values,
                [key]: newval
                },
                errors: {
                    ...state.errors,
                    [key]: false
                }
            }));
            if (!hasChanges && key !== "setExpiration") dispatch(hasChanged());
            if (key === "setExpiration") onValueChange("expiryDate", null);
            if (key === "swtype") onValueChange("ecoRating", null);
            if (entity === "users" && editMode && key === "orgAlias") onValueChange("grant", find(state.values.grants, { orgAlias: value }) && find(state.values.grants, { orgAlias: value }).grantName || "partner")
        };
    };

    const validateValue = (key, value) => {
        const { fields } = formFields[entity];
        const item = find(flatten(fields), { key: key });
        let checked = true;

        if (item && item.rules) {
            item.rules.map(rule => {
                if (rule.pattern) {
                    if (value === null || rule.pattern.test(rule.key ? value[rule.key] : value)) {
                        setState(state => ({
                            ...state,
                            errors: {
                                ...state.errors,
                                [key]: false
                            }
                        }));
                    } else {
                        checked = false;
                        setState(state => ({
                            ...state,
                            errors: {
                                ...state.errors,
                                [key]: rule.message
                            }
                        }));
                    };
                }
            });
        };

        return checked;
    };

    const validateValues = () => {
        const { fields } = formFields[entity];
        const { values, errors } = state;
        let newerrors = clone(errors);
        let fieldsToValidate = flatten(fields);

        return new Promise((resolve, reject) => {
            fieldsToValidate.map(field => {
                const { key, rules, label, multiple } = field;
                if (rules) {
                    rules.map(rule => {
                        let value = key == "files" || key == "log"
                            ? values[key] && !multiple ? values[key].name : values[key]
                            : values[key];
                        if (value && !multiple && rule.pattern) {
                            if (value !== null && !rule.pattern.test(value)) {
                                if (!newerrors[key]) {
                                    newerrors = Object.assign({}, newerrors, { [key]: rule.message });
                                };
                            };
                        }
                        else if (rule.required) {
                            if (value === null || value === undefined || (typeof value == "string" && !value.trim().length) || (Array.isArray(value) && !value.length)) {
                                if (!newerrors[key]) {
                                    newerrors = Object.assign({}, newerrors, { [key]: `${label} is required.`})
                                };
                            };
                        };
                    });
                };
            });
            if (filter(Object.values(newerrors), o => o !== false).length) {
                setState(state => ({ ...state, errors: newerrors }));
                return reject(false);
            } else {
                return resolve(true);
            };
        });
    };

    const onSave = () => {
        const { values } = state;
        validateValues()
            .then(success => {
                let body = {};
                if (entity === "users") {
                    const { email, name, orgAlias, grant, expiry } = values;
                    body = JSON.stringify({
                        email: email,
                        full_name: name,
                        org_alias: orgAlias,
                        grant: editMode ? undefined :  grant,
                        expiry_date: expiry
                    });
                }
                else if (entity === "issues") {
                    const { summary, description, product, type, log, file, osversion, sysarch, version } = values;
                    const fmData = new FormData();
                    fmData.append("data", JSON.stringify({
                        summary: summary,
                        description: description,
                        product: product,
                        issuetype: type,
                        osversion: osversion,
                        sysarch: sysarch,
                        version: version
                    }));

                    if (log) {
                        fmData.append("log", log);
                    };
                    if (file) {
                        fmData.append("attachment", file);
                    };
                    body = fmData;
                }
                else if (entity === "organisations") {
                    const { alias, name, addr, phone, email, notes } = values;
                    body = JSON.stringify({
                        name: name,
                        alias: alias,
                        addr: addr,
                        phone: phone,
                        email: email,
                        notes: notes
                    });
                }
                else if (entity === "grants") {
                    const { email, orgAlias, grantName, expiryDate } = values;
                    body = JSON.stringify({
                        email: email,
                        org_alias: orgAlias,
                        grant: grantName,
                        expiry_date: noneToNull(expiryDate)
                    });
                }
                else if (entity === "contracts") {
                    const { alias, name, endDate } = values;
                    body = JSON.stringify({
                        org_alias: alias,
                        contract_name: name,
                        end_date: endDate
                    });
                }
                else if (entity === "packages") {
                    const { key, name, version, sysarch, minos, style, swtype, description, restart, files, ecoRating, contract } = values;
                    if (editMode) {
                        body = JSON.stringify({
                            package_number: key,
                            name: name.toUpperCase(),
                            description: description,
                            version: version.toUpperCase(),
                            minos: minos,
                            sysarch: sysarch,
                            style: style.toUpperCase(),
                            swtype: swtype.toUpperCase(),
                            restart_required: Number(restart),
                            eco_rating: swtype === "e" && ecoRating ? Number(ecoRating) : undefined
                        })
                    } else {
                        body.id = `pkg-upload-${Date.now()}`;
                        body.data = {
                            name: name.toUpperCase(),
                            description: description,
                            version: version.toUpperCase(),
                            minos: minos,
                            sysarch: sysarch,
                            style: style.toUpperCase(),
                            swtype: swtype.toUpperCase(),
                            prereq: null,
                            restart_required: Number(restart),
                            eco_rating: swtype === "e" && ecoRating ? Number(ecoRating) : undefined,
                            contract: contract
                        };
                        body.files = files;
                    }
                }
                else if (entity === "restrictions") {
                    const { key, contracts } = state.values;
                    body = JSON.stringify({
                        package_number: key,
                        contracts: contracts.map(c => c.name)
                    });
                }
                else if (entity === "prereq") {
                    const { key, prereq } = values;
                    body = JSON.stringify({
                        package_number: key,
                        prereq: prereq.map((pr, idx) => ({
                            package_number: pr.packageNumber,
                            order: ++idx,
                            name: pr.name,
                            version: pr.version
                        }))
                    });
                }
                else if (entity === "contract") {
                    const { name, description } = values;
                    body = JSON.stringify({
                        name: name,
                        description: description
                    });
                };

                if (entity === "packages" && !editMode) {
                    dispatch(registerPackage({ data: body.data, files: body.files}));
                } else {
                    dispatch(addItem({ entity: entity, values: body }));
                };
            })
            .catch(error => console.log("Validation failed with values: ", values));
    };

    return(
        Object.keys(formFields).indexOf(entity) > -1 && entity
            ? <ActionForm
                title={formFields[entity].title}
                fields={formFields[entity].fields}
                values={state.values}
                errors={state.errors}
                onClose={() => dispatch(showForm(false))}
                onValueChange={onValueChange}
                onSave={onSave}
                hasChanges={hasChanges}
                width={formFields[entity].width || 500}
                okButtonText={formFields[entity].okButtonText}
                validateValue={validateValue}
            />
            : null
    );
};

export { ActionFormContainer };